
/**
 * Time conversion utilities for handling 12-hour and 24-hour formats
 */

export function convert24to12(time24: string): string {
  if (!time24 || !time24.includes(':')) return time24;
  
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const minute = minutes || '00';
  
  if (hour === 0) {
    return `12:${minute} AM`;
  } else if (hour < 12) {
    return `${hour}:${minute} AM`;
  } else if (hour === 12) {
    return `12:${minute} PM`;
  } else {
    return `${hour - 12}:${minute} PM`;
  }
}

export function convert12to24(time12: string): string {
  if (!time12 || !time12.includes(':')) return time12;
  
  const [timePart, ampm] = time12.split(' ');
  if (!ampm) return time12; // Already 24-hour format
  
  const [hours, minutes] = timePart.split(':');
  let hour = parseInt(hours, 10);
  
  if (ampm.toUpperCase() === 'AM') {
    if (hour === 12) hour = 0;
  } else if (ampm.toUpperCase() === 'PM') {
    if (hour !== 12) hour += 12;
  }
  
  return `${hour.toString().padStart(2, '0')}:${minutes}`;
}

export function validateTimeFormat(time: string, format: '12' | '24'): boolean {
  if (!time) return false;
  
  if (format === '24') {
    // 24-hour format: HH:MM
    const regex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return regex.test(time);
  } else {
    // 12-hour format: H:MM AM/PM or HH:MM AM/PM
    const regex = /^(1[0-2]|[1-9]):[0-5][0-9] (AM|PM)$/i;
    return regex.test(time);
  }
}

export function getTimeFormatPlaceholder(format: '12' | '24'): string {
  return format === '24' ? 'HH:MM' : 'H:MM AM/PM';
}

// Format time in 12-hour format
export function formatTime12(time: string): string {
  if (!time) return '';
  return convert24to12(time);
}

// Format time in 24-hour format
export function formatTime24(time: string): string {
  if (!time) return '';
  return time; // 24-hour format is already in HH:MM format
}

// Format time based on specified format
export function formatTime(time: string, timeFormat: '12' | '24' = '12'): string {
  return timeFormat === '24' ? formatTime24(time) : formatTime12(time);
}
